import { Check, ChevronDown } from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { UseCaseSummary } from '../../types';
import useCaseService from '../../services/useCaseService';

interface UseCaseSelectorProps {
  domainId: string | null;
  selectedUseCaseId: string | null;
  onUseCaseChange: (useCase: UseCaseSummary | null) => void;
  className?: string;
  disabled?: boolean;
}

export const UseCaseSelector: React.FC<UseCaseSelectorProps> = ({
  domainId,
  selectedUseCaseId,
  onUseCaseChange,
  className = '',
  disabled = false,
}) => {
  const { t } = useTranslation();
  const [useCases, setUseCases] = useState<UseCaseSummary[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const loadUseCases = async () => {
    if (!domainId) return;
    
    setIsLoading(true);
    try {
      const response = await useCaseService.list({ domain_id: domainId });
      setUseCases(response);
    } catch (error) {
      console.error('Error loading use cases:', error);
      setUseCases([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (domainId) {
      loadUseCases();
    } else {
      setUseCases([]);
      onUseCaseChange(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domainId]);

  const selectedUseCase = useCases.find(uc => uc.id === selectedUseCaseId);

  const handleSelect = (useCase: UseCaseSummary) => {
    onUseCaseChange(useCase);
    setIsOpen(false);
  };

  if (!domainId) {
    return (
      <div className={`text-sm text-muted-foreground ${className}`}>
        {t('selectors.selectDomainFirst')}
      </div>
    );
  }

  return (
    <div className={`relative inline-block text-left w-full ${className}`}>
      <div>
        <button
          type="button"
          className="inline-flex w-full justify-between items-center rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground shadow-sm hover:bg-accent hover:text-accent-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled || isLoading}
        >
          {isLoading ? (
            <span className="truncate">{t('selectors.loading')}</span>
          ) : selectedUseCase ? (
            <span className="truncate" title={selectedUseCase.name}>{selectedUseCase.name}</span>
          ) : (
            <span className="text-muted-foreground truncate">{t('selectors.selectUseCase')}</span>
          )}
          <ChevronDown className="ml-2 h-5 w-5 flex-shrink-0" aria-hidden="true" />
        </button>
      </div>

      {isOpen && !disabled && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute z-20 mt-2 w-full rounded-md border border-border bg-popover shadow-lg">
            <div className="py-1 max-h-60 overflow-y-auto">
              {useCases.length === 0 ? (
                <div className="px-4 py-2 text-sm text-muted-foreground">
                  {t('selectors.noUseCaseInDomain')}
                </div>
              ) : (
                useCases.map((useCase) => (
                  <button
                    key={useCase.id}
                    className="w-full text-left px-4 py-2 text-sm hover:bg-accent hover:text-accent-foreground flex items-center justify-between"
                    onClick={() => handleSelect(useCase)}
                  >
                    <span className="truncate" title={useCase.name}>{useCase.name}</span>
                    {useCase.id === selectedUseCaseId && (
                      <Check className="h-5 w-5 flex-shrink-0 text-primary ml-2" />
                    )}
                  </button>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};
